import { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";
import { C } from "../constants/colors";
import { TYPE_STYLE } from "../constants/opportunities";
import { formatDate, profileInitials } from "../lib/utils";
import { Logo } from "../components/ui/Logo";
import { SearchScreen } from "./SearchScreen";
import { MessagingScreen } from "./MessagingScreen";
import { TrialBanner } from "../components/TrialBanner";
import { EditProfileModal } from "../components/EditProfileModal";
import { PostCallForm } from "../components/PostCallForm";
import { ExhibitionHistorySection } from "../components/ExhibitionHistorySection";
import { EmptyCallouts } from "../components/EmptyCallouts";
import { ConsignmentConfirmModal } from "../components/ConsignmentConfirmModal";
import { PlanManagement } from "../components/PlanManagement";

const IcoHome = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);
const IcoWork = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2"/>
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
    <line x1="9" y1="12" x2="15" y2="12"/>
    <line x1="9" y1="16" x2="13" y2="16"/>
  </svg>
);
const IcoSearch = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/>
    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);
const IcoMsg = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
  </svg>
);
const IcoProfile = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);
const IcoMenu = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="6" x2="21" y2="6"/>
    <line x1="3" y1="12" x2="21" y2="12"/>
    <line x1="3" y1="18" x2="21" y2="18"/>
  </svg>
);

const MOBILE_NAV_ITEMS = [
  { id: "home",     Icon: IcoHome    },
  { id: "work",     Icon: IcoWork    },
  { id: "search",   Icon: IcoSearch  },
  { id: "messages", Icon: IcoMsg     },
  { id: "profile",  Icon: IcoProfile },
];

export function GalleryMini({ onPublic, onLogout, bp, profile: profileProp }) {
  const [tab, setTab] = useState("callouts");
  const [search, setSearch] = useState(false);
  const [callouts, setCallouts] = useState([]);
  const [applications, setApplications] = useState([]);
  const [showPostForm, setShowPostForm] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [currentProfile, setCurrentProfile] = useState(profileProp);
  const [subscribing, setSubscribing] = useState(null); // 'emerging' | 'established' | null
  const [subscribeError, setSubscribeError] = useState(null);
  const [subSuccessToast, setSubSuccessToast] = useState(false);

  // Handle return from Stripe Checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('subscription') === 'success') {
      setSubSuccessToast(true);
      setTimeout(() => setSubSuccessToast(false), 5000);
      // Re-fetch profile so subscription_status reflects the webhook update
      supabase.from('profiles').select('*').eq('id', profileProp?.id).single()
        .then(({ data }) => { if (data) setCurrentProfile(data); });
      // Clean up URL param
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (profileProp) setCurrentProfile(profileProp);
  }, [profileProp]);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [appLoading, setAppLoading] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [pendingAcceptApp, setPendingAcceptApp] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedCallout, setSelectedCallout] = useState(null);
  const [togglingStatus, setTogglingStatus] = useState(false);
  const [editingCallout, setEditingCallout] = useState(null);
  const [calloutsLoaded, setCalloutsLoaded] = useState(false);
  const [appsLoaded, setAppsLoaded] = useState(false);
  const [hamburgerOpen, setHamburgerOpen] = useState(false);
  const [galProfileSubView, setGalProfileSubView] = useState(null);
  const [mobileNav, setMobileNav] = useState("home");

  const profile = currentProfile;
  const displayName = profile?.display_name || profile?.full_name || 'New Gallery';
  const initials = profileInitials(profile);

  // Freemium model: free-tier galleries can post up to 3 active listings.
  // Paid = active or trialing subscription on emerging/established.
  const isOnPaidPlan =
    ['active', 'trialing'].includes(profile?.subscription_status) &&
    ['emerging', 'established'].includes(profile?.plan);
  const activeCalloutCount = callouts.filter(c => c.status === 'active').length;
  const calloutLimitReached = !isOnPaidPlan && activeCalloutCount >= 3;

  const handleSubscribe = async (plan) => {
    setSubscribeError(null);
    setSubscribing(plan);
    try {
      // getSession reads from local cache — never makes a network call that can fail mid-session
      const { data: { session } } = await supabase.auth.getSession();
      const email = session?.user?.email;
      if (!email) {
        console.error('handleSubscribe: no session email — user may need to re-login');
        setSubscribeError('Session error — please log out and back in.');
        return;
      }
      const res = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ galleryId: profile.id, plan, email }),
      });
      const data = await res.json();
      if (data.sessionUrl) {
        window.location.href = data.sessionUrl;
      } else {
        console.error('create-checkout-session: no sessionUrl returned', data);
        setSubscribeError(data.error || 'Could not start checkout. Please try again.');
      }
    } catch (err) {
      console.error('handleSubscribe error:', err);
      setSubscribeError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubscribing(null);
    }
  };

  const refreshCallouts = () => {
    if (!profile) return;
    supabase
      .from('opportunities')
      .select('*')
      .eq('gallery_id', profile.id)
      .order('created_at', { ascending: false })
      .then(({ data, error }) => { if (error) console.error('stats:callouts', error); if (data) setCallouts(data); setCalloutsLoaded(true); });
  };

  // Fetch gallery's open calls
  useEffect(() => { refreshCallouts(); }, [profile]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshApplications = () => {
    if (!profile) return;
    supabase
      .from('applications')
      .select('*, opportunities!inner(gallery_id, title), profiles:artist_id(full_name, display_name)')
      .eq('opportunities.gallery_id', profile.id)
      .order('submitted_at', { ascending: false })
      .then(({ data, error }) => { if (error) console.error('stats:apps', error); if (data) setApplications(data); setAppsLoaded(true); });
  };

  // Fetch applications to gallery's opportunities
  useEffect(() => { refreshApplications(); }, [profile]); // eslint-disable-line react-hooks/exhaustive-deps

  // Unread message count + real-time badge updates
  useEffect(() => {
    if (!profile) return;
    supabase.from('messages').select('id').eq('to_user_id', profile.id).eq('read', false)
      .then(({ data }) => setUnreadCount(data?.length || 0));
    const channel = supabase.channel(`unread-gallery-${profile.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, payload => {
        if (payload.new.to_user_id === profile.id) setUnreadCount(n => n + 1);
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [profile]); // eslint-disable-line react-hooks/exhaustive-deps

  const openApplication = async (app) => {
    setAppLoading(true);
    const { data } = await supabase
      .from('applications')
      .select('*, opportunities(id, title, gallery_id, commission, consignment_duration, damage_policy, return_shipping, payment_terms, consignment_offered), artist:profiles!artist_id(*)')
      .eq('id', app.id)
      .single();
    setSelectedApp(data || app);
    setAppLoading(false);
  };

  const updateStatus = async (status) => {
    if (!selectedApp) return;
    setUpdatingStatus(true);
    await supabase.from('applications').update({ status }).eq('id', selectedApp.id);
    setUpdatingStatus(false);
    refreshApplications();
    setSelectedApp(null);
  };

  const handleTabChange = (id) => {
    setTab(id);
    setHamburgerOpen(false);
    if (id !== "profile") setGalProfileSubView(null);
    if (id === 'messages' && unreadCount > 0) {
      setUnreadCount(0);
      supabase.from('messages').update({ read: true }).eq('to_user_id', profile.id).eq('read', false);
    }
  };

  const handleMobileNav = (id) => {
    setMobileNav(id);
    setHamburgerOpen(false);
    if (id !== "profile") setGalProfileSubView(null);
    if (id === "messages" && unreadCount > 0) {
      setUnreadCount(0);
      supabase.from('messages').update({ read: true }).eq('to_user_id', profile.id).eq('read', false);
    }
  };

  const toggleCalloutStatus = async () => {
    if (!selectedCallout || togglingStatus) return;
    const newStatus = selectedCallout.status === 'active' ? 'closed' : 'active';
    setTogglingStatus(true);
    const { error } = await supabase.from('opportunities').update({ status: newStatus }).eq('id', selectedCallout.id);
    setTogglingStatus(false);
    if (!error) {
      const updated = { ...selectedCallout, status: newStatus };
      setSelectedCallout(updated);
      setCallouts(cs => cs.map(c => c.id === selectedCallout.id ? updated : c));
    }
  };

  const STATUS_COLORS = { pending:{bg:C.amberL,c:C.amber}, shortlisted:{bg:"#e8d5f5",c:"#5a2d82"}, accepted:{bg:C.sageL,c:C.sageD}, declined:{bg:C.roseL,c:C.rose} };
  const fmtPrize = v => { const n = Number(String(v||'').replace(/[,$\s]/g,'')); return v ? (isNaN(n) ? v : `$${n.toLocaleString()}`) : "—"; };
  const fmtCommission = v => v ? `${v}%` : "—";
  const fmtResponsibility = v => v ? `${v.charAt(0).toUpperCase()}${v.slice(1)} is responsible` : "—";

  const TABS = [
    { id:"callouts",     label:"Open Calls",   icon:"📢" },
    { id:"applications", label:"Applications", icon:"▤"  },
    { id:"messages",     label:"Messages",     icon:"✉"  },
    { id:"plan",         label:"Plan",         icon:"💳" },
    { id:"profile",      label:"Profile",      icon:"🏛️" },
  ];

  // Desktop search replaces entire UI
  if (search) return <SearchScreen onBack={()=>setSearch(false)} bp={bp} opportunities={[]} artists={[]} currentProfile={profile}/>;
  // Mobile search also replaces entire UI
  if (bp === "mobile" && mobileNav === "search") return <SearchScreen onBack={()=>setMobileNav("home")} bp={bp} opportunities={[]} artists={[]} currentProfile={profile}/>;

  // Maps mobile nav state onto the same tab IDs the content blocks use
  const effectiveTab = bp === "mobile"
    ? mobileNav === "home"     ? "callouts"
    : mobileNav === "messages" ? "messages"
    : mobileNav === "work"     ? "applications"
    : mobileNav === "profile"  ? "profile"
    : "callouts"
    : tab;

  return (
    <>
      {subSuccessToast && (
        <div style={{ position:"fixed",bottom:24,left:"50%",transform:"translateX(-50%)",zIndex:400,background:C.sageD,color:"#fff",borderRadius:8,padding:"12px 22px",fontSize:".84rem",fontFamily:"'DM Sans',sans-serif",boxShadow:"0 4px 20px rgba(0,0,0,.25)",whiteSpace:"nowrap" }}>
          ✓ Subscription activated — welcome aboard!
        </div>
      )}
      {/* Topnav */}
      <nav className="topnav">
        {bp === "mobile" && mobileNav === "profile" ? (
          <>
            <button onClick={()=>handleMobileNav("home")} style={{background:"none",border:"none",padding:0,cursor:"pointer"}}><Logo/></button>
            <button onClick={()=>setHamburgerOpen(true)} style={{background:"none",border:"none",padding:4,color:"rgba(196,168,130,.7)",lineHeight:0,cursor:"pointer"}}><IcoMenu/></button>
          </>
        ) : (
          <>
            <button onClick={()=>bp==="mobile"?handleMobileNav("home"):setTab("callouts")} style={{ background:"none",border:"none",padding:0,cursor:"pointer" }}><Logo/></button>
            {bp!=="mobile"&&(
              <div style={{ display:"flex",gap:4 }}>
                {TABS.map(t=>(
                  <button key={t.id} className={`nav-lnk ${tab===t.id?"on":""}`} onClick={()=>handleTabChange(t.id)}>
                    <span style={{ position:"relative" }}>
                      {t.label}
                      {t.id==="messages" && unreadCount > 0 && tab !== "messages" && (
                        <span style={{ position:"absolute",top:-8,right:-14,background:C.rose,color:"#fff",borderRadius:"50%",width:16,height:16,fontSize:".46rem",fontFamily:"'DM Mono',monospace",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,lineHeight:1 }}>
                          {unreadCount > 9 ? "9+" : unreadCount}
                        </span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            )}
            {bp!=="mobile"&&(
              <div style={{ display:"flex",gap:8,alignItems:"center" }}>
                <button onClick={()=>setSearch(true)} style={{ background:"rgba(255,255,255,.08)",border:"none",borderRadius:4,padding:"6px 10px",color:C.mutedL,fontSize:".8rem",minHeight:36,display:"flex",alignItems:"center",gap:5 }}>🔍</button>
                <button onClick={onLogout} className="btn-dk" style={{ fontSize:".64rem",padding:"7px 12px",minHeight:36 }}>Log out</button>
              </div>
            )}
          </>
        )}
      </nav>

      <TrialBanner userType="gallery" onSubscribe={handleSubscribe} trialEndsAt={profile?.trial_ends_at} subscriptionStatus={profile?.subscription_status} subscribing={subscribing}/>

      <div style={{ maxWidth:1060,margin:"0 auto",padding:bp==="mobile"?"20px 16px 100px":"28px 24px 80px" }}>

        {subscribeError && (
          <div style={{ background:C.roseL,border:`1px solid ${C.rose}`,borderRadius:6,padding:"10px 14px",marginBottom:16,fontSize:".8rem",color:C.rose,display:"flex",justifyContent:"space-between",alignItems:"center" }}>
            <span>⚠ {subscribeError}</span>
            <button onClick={()=>setSubscribeError(null)} style={{ background:"none",border:"none",color:C.rose,cursor:"pointer",fontSize:"1rem",lineHeight:1,padding:"0 0 0 12px" }}>✕</button>
          </div>
        )}

        {/* Back button when inside a profile sub-view on mobile */}
        {bp === "mobile" && mobileNav === "profile" && galProfileSubView && (
          <button
            onClick={()=>setGalProfileSubView(null)}
            style={{ display:"flex",alignItems:"center",gap:6,background:"none",border:"none",padding:"0 0 20px",color:C.muted,fontSize:".76rem",fontFamily:"'DM Mono',monospace",letterSpacing:"1px",cursor:"pointer" }}
          >
            ← Back
          </button>
        )}

        {/* Stats row — only on Open Calls / Applications tab */}
        {(effectiveTab === "callouts" || effectiveTab === "applications") && (()=>{
          const activeCallouts = callouts.filter(c=>c.status==='active').length;
          const pendingApps    = applications.filter(a=>a.status==='pending').length;
          const tiles = [
            { label:"Active Calls",   value:calloutsLoaded ? activeCallouts        : "--" },
            { label:"Total Calls",    value:calloutsLoaded ? callouts.length        : "--" },
            { label:"Applications",   value:appsLoaded     ? applications.length    : "--" },
            { label:"Pending Review", value:appsLoaded     ? pendingApps            : "--" },
          ];
          return (
            <div style={{ display:"grid",gridTemplateColumns:bp==="mobile"?"repeat(2,1fr)":"repeat(4,1fr)",gap:10,marginBottom:28 }}>
              {tiles.map(({label,value})=>(
                <div key={label} style={{ background:C.cream,border:`1px solid ${C.sand}`,borderRadius:8,padding:"14px 16px" }}>
                  <div style={{ fontFamily:"'DM Mono',monospace",fontSize:"1.6rem",fontWeight:600,color:C.bark,marginBottom:4,lineHeight:1 }}>{value}</div>
                  <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".58rem",letterSpacing:"1.5px",textTransform:"uppercase",color:C.muted }}>{label}</div>
                </div>
              ))}
            </div>
          );
        })()}

        {effectiveTab==="callouts"&&(
          callouts.length > 0 ? (
            <div>
              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:22 }}>
                <h1 style={{ fontFamily:"'Fraunces',serif",fontSize:"1.7rem",fontWeight:400,color:C.bark }}>My Open Calls</h1>
                <div style={{ display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4 }}>
                  <button className="btn-p" style={{ fontSize:".68rem",padding:"9px 16px",minHeight:40,flexShrink:0 }} onClick={()=>calloutLimitReached?setShowUpgradePrompt(true):setShowPostForm(true)}>+ Post Open Call</button>
                  {calloutLimitReached&&<div style={{ fontSize:".68rem",color:C.muted,fontFamily:"'DM Mono',monospace",maxWidth:260,textAlign:"right",lineHeight:1.4 }}>
                    Free tier limit reached. <button onClick={()=>handleSubscribe('emerging')} style={{ background:"none",border:"none",color:C.clay,cursor:"pointer",textDecoration:"underline",fontFamily:"inherit",fontSize:"inherit",padding:0 }}>Upgrade to Emerging</button> for unlimited.
                  </div>}
                </div>
              </div>
              <div style={{ display:"flex",flexDirection:"column",gap:10 }}>
                {callouts.map(c=>{
                  const ts = TYPE_STYLE[c.type] || TYPE_STYLE.exhibition;
                  return (
                    <div key={c.id} onClick={()=>setSelectedCallout(c)} style={{ background:C.cream,border:`1px solid ${C.sand}`,borderRadius:8,padding:"16px",cursor:"pointer" }}>
                      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12 }}>
                        <div>
                          <span style={{ background:ts.bg,color:ts.color,fontFamily:"'DM Mono',monospace",fontSize:".56rem",letterSpacing:"1.5px",textTransform:"uppercase",padding:"2px 8px",borderRadius:"2px",fontWeight:600 }}>{ts.label}</span>
                          <div style={{ fontFamily:"'Fraunces',serif",fontSize:"1rem",color:C.bark,marginTop:6,marginBottom:3 }}>{c.title}</div>
                          <div style={{ fontSize:".7rem",color:C.muted,fontFamily:"'DM Mono',monospace" }}>Deadline {c.deadline ? formatDate(c.deadline) : '—'} · {c.location || 'Australia'}</div>
                        </div>
                        <span style={{ background:c.status==='active'?C.sageL:C.parchD,color:c.status==='active'?C.sageD:C.muted,fontFamily:"'DM Mono',monospace",fontSize:".56rem",letterSpacing:"1px",textTransform:"uppercase",padding:"3px 9px",borderRadius:"2px",fontWeight:600,flexShrink:0 }}>{c.status}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <>
              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:22 }}>
                <div>
                  <h1 style={{ fontFamily:"'Fraunces',serif",fontSize:"1.7rem",fontWeight:400,color:C.bark,marginBottom:4 }}>My Open Calls</h1>
                  <p style={{ fontSize:".84rem",color:C.muted }}>Post and manage your exhibitions, grants and residencies.</p>
                </div>
                <div style={{ display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4 }}>
                  <button className="btn-p" style={{ fontSize:".68rem",padding:"9px 16px",minHeight:40,flexShrink:0 }} onClick={()=>calloutLimitReached?setShowUpgradePrompt(true):setShowPostForm(true)}>+ Post Open Call</button>
                  {calloutLimitReached&&<div style={{ fontSize:".68rem",color:C.muted,fontFamily:"'DM Mono',monospace",maxWidth:260,textAlign:"right",lineHeight:1.4 }}>
                    Free tier limit reached. <button onClick={()=>handleSubscribe('emerging')} style={{ background:"none",border:"none",color:C.clay,cursor:"pointer",textDecoration:"underline",fontFamily:"inherit",fontSize:"inherit",padding:0 }}>Upgrade to Emerging</button> for unlimited.
                  </div>}
                </div>
              </div>
              <EmptyCallouts onPost={()=>calloutLimitReached?setShowUpgradePrompt(true):setShowPostForm(true)}/>
            </>
          )
        )}

        {effectiveTab==="applications"&&(
          applications.length > 0 ? (
            <div>
              <h1 style={{ fontFamily:"'Fraunces',serif",fontSize:"1.7rem",fontWeight:400,color:C.bark,marginBottom:20 }}>Applications</h1>
              <div style={{ display:"flex",flexDirection:"column",gap:10 }}>
                {applications.map(app=>{
                  const artistName = app.profiles?.display_name || app.profiles?.full_name || 'Artist';
                  const sc = STATUS_COLORS[app.status] || STATUS_COLORS.pending;
                  return (
                    <div key={app.id} onClick={()=>openApplication(app)} style={{ background:C.cream,border:`1px solid ${C.sand}`,borderRadius:8,padding:"16px",cursor:"pointer" }}>
                      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12 }}>
                        <div>
                          <div style={{ fontFamily:"'Fraunces',serif",fontSize:"1rem",color:C.bark,marginBottom:3 }}>{artistName}</div>
                          <div style={{ fontSize:".7rem",color:C.muted,fontFamily:"'DM Mono',monospace" }}>{app.opportunities?.title} · {new Date(app.submitted_at).toLocaleDateString('en-AU')}</div>
                        </div>
                        <span style={{ background:sc.bg,color:sc.c,fontFamily:"'DM Mono',monospace",fontSize:".56rem",letterSpacing:"1.5px",textTransform:"uppercase",padding:"3px 9px",borderRadius:"2px",fontWeight:600,flexShrink:0 }}>{app.status}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div>
              <h1 style={{ fontFamily:"'Fraunces',serif",fontSize:"1.7rem",fontWeight:400,color:C.bark,marginBottom:22 }}>Applications</h1>
              <div className="empty-state">
                <div style={{ width:72,height:72,borderRadius:"50%",background:C.parchD,display:"flex",alignItems:"center",justifyContent:"center",fontSize:"1.8rem",marginBottom:20 }}>▤</div>
                <h2 style={{ fontFamily:"'Fraunces',serif",fontSize:"1.4rem",fontWeight:400,color:C.bark,marginBottom:10 }}>No applications yet</h2>
                <p style={{ fontSize:".84rem",color:C.muted,lineHeight:1.75,maxWidth:380,marginBottom:24 }}>Once you post an open call, applications from artists will appear here.</p>
                <button className="btn-p" onClick={()=>bp==="mobile"?handleMobileNav("home"):setTab("callouts")}>Post an open call →</button>
              </div>
            </div>
          )
        )}

        {effectiveTab==="messages"&&(
          <MessagingScreen profile={profile} bp={bp}/>
        )}

        {effectiveTab==="plan"&&(
          <PlanManagement profile={profile} userType="gallery" onUpgrade={handleSubscribe}/>
        )}

        {effectiveTab==="profile"&&(
          bp === "mobile" ? (
            galProfileSubView === "plan" ? (
              <div>
                <PlanManagement profile={profile} userType="gallery" onUpgrade={handleSubscribe}/>
              </div>
            ) : (
              /* Mobile Instagram-style gallery profile */
              <div>
                {/* Profile header */}
                <div style={{display:"flex",flexDirection:"column",alignItems:"center",paddingBottom:24,borderBottom:`1px solid ${C.sand}`,marginBottom:20}}>
                  {profile?.avatar_url
                    ? <img src={profile.avatar_url} alt="" style={{width:88,height:88,borderRadius:"50%",objectFit:"cover",border:`3px solid ${C.sand}`,marginBottom:14}}/>
                    : <div style={{width:88,height:88,borderRadius:"50%",background:`linear-gradient(135deg,${C.clay},${C.barkL})`,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Mono',monospace",fontSize:"1.4rem",color:"#fff",marginBottom:14,border:`3px solid ${C.sand}`}}>{initials}</div>
                  }
                  <div style={{fontFamily:"'Fraunces',serif",fontSize:"1.4rem",fontWeight:400,color:C.bark,marginBottom:5,textAlign:"center"}}>{displayName}</div>
                  {profile?.location&&(
                    <div style={{fontSize:".76rem",color:C.muted,marginBottom:profile?.bio?10:14}}>{profile.location}</div>
                  )}
                  {profile?.bio&&(
                    <p style={{fontSize:".82rem",color:C.bark,lineHeight:1.65,margin:"0 0 14px",alignSelf:"flex-start"}}>{profile.bio}</p>
                  )}
                  <button className="btn-g" style={{fontSize:".62rem",padding:"7px 18px",minHeight:36,alignSelf:"flex-start"}} onClick={()=>setShowEditProfile(true)}>Edit Profile</button>
                </div>
                {/* Open calls grid */}
                <span className="slbl">Open Calls</span>
                {(()=>{
                  const active = callouts.filter(c=>c.status==='active');
                  return active.length > 0 ? (
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginTop:8}}>
                      {active.map(c=>{
                        const ts = TYPE_STYLE[c.type]||TYPE_STYLE.exhibition;
                        return (
                          <div key={c.id} style={{background:C.cream,border:`1px solid ${C.sand}`,borderRadius:8,padding:"12px"}}>
                            <span style={{background:ts.bg,color:ts.color,fontFamily:"'DM Mono',monospace",fontSize:".5rem",letterSpacing:"1.5px",textTransform:"uppercase",padding:"2px 7px",borderRadius:"2px",fontWeight:600}}>{ts.label}</span>
                            <div style={{fontFamily:"'Fraunces',serif",fontSize:".88rem",color:C.bark,marginTop:6,marginBottom:4,lineHeight:1.3}}>{c.title}</div>
                            <div style={{fontSize:".62rem",color:C.muted,fontFamily:"'DM Mono',monospace"}}>{c.deadline ? formatDate(c.deadline) : '—'}</div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p style={{fontSize:".82rem",color:C.muted,textAlign:"center",padding:"32px 0"}}>
                      No active open calls — post one to get started
                    </p>
                  );
                })()}
              </div>
            )
          ) : (
            /* Desktop profile — unchanged */
            <div>
              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:22 }}>
                <h1 style={{ fontFamily:"'Fraunces',serif",fontSize:"1.7rem",fontWeight:400,color:C.bark }}>Gallery Profile</h1>
                <button type="button" className="btn-p" style={{ fontSize:".66rem",padding:"8px 14px",minHeight:40 }} onClick={()=>setShowEditProfile(true)}>Edit Profile</button>
              </div>
              <div style={{ background:`linear-gradient(120deg,${C.bark},${C.barkL})`,borderRadius:8,padding:"24px",marginBottom:22,position:"relative",overflow:"hidden" }}>
                <div style={{ position:"absolute",bottom:-12,right:-4,fontFamily:"'DM Mono',monospace",fontSize:"3.5rem",color:"rgba(193,127,74,.055)",whiteSpace:"nowrap",letterSpacing:"-2px",pointerEvents:"none" }}>opencall.</div>
                <div style={{ display:"flex",gap:16,alignItems:"flex-start",position:"relative",zIndex:1 }}>
                  {profile?.avatar_url
                    ? <img src={profile.avatar_url} alt="" style={{ width:60,height:60,borderRadius:10,objectFit:"cover",flexShrink:0,border:"3px solid rgba(193,127,74,.3)" }}/>
                    : <div style={{ width:60,height:60,borderRadius:10,background:`linear-gradient(135deg,${C.clay},${C.barkL})`,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Mono',monospace",fontSize:".95rem",color:"#fff",flexShrink:0,border:"3px solid rgba(193,127,74,.3)" }}>{initials}</div>
                  }
                  <div style={{ flex:1,minWidth:0 }}>
                    <div style={{ fontFamily:"'Fraunces',serif",fontSize:"1.5rem",color:"#f0e2cc",fontWeight:400,marginBottom:3 }}>{displayName}</div>
                    <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".62rem",color:C.clay,letterSpacing:"2px",textTransform:"uppercase",marginBottom:6 }}>Gallery{profile?.location ? ` · ${profile.location}` : ''}</div>
                    {profile?.bio
                      ? <p style={{ fontSize:".82rem",color:"rgba(196,168,130,.8)",lineHeight:1.65,margin:0 }}>{profile.bio}</p>
                      : <p style={{ fontSize:".82rem",color:"rgba(196,168,130,.5)",lineHeight:1.65,margin:0 }}>Complete your gallery profile so artists know who they're applying to.</p>
                    }
                    <div style={{ display:"flex",gap:14,marginTop:10,flexWrap:"wrap" }}>
                      {profile?.website && <a href={profile.website} target="_blank" rel="noreferrer" style={{ fontSize:".72rem",color:C.clay,fontFamily:"'DM Mono',monospace",textDecoration:"none" }}>🌐 {profile.website.replace(/^https?:\/\//,'')}</a>}
                      {profile?.instagram && <a href={`https://instagram.com/${profile.instagram}`} target="_blank" rel="noreferrer" style={{ fontSize:".72rem",color:C.clay,fontFamily:"'DM Mono',monospace",textDecoration:"none" }}>📷 @{profile.instagram}</a>}
                    </div>
                  </div>
                </div>
              </div>
              <ExhibitionHistorySection
                profile={profile}
                onHistorySaved={(exhibition_history)=>setCurrentProfile(p=>({...p,exhibition_history}))}
              />
            </div>
          )
        )}
      </div>

      {/* Bottom nav */}
      {bp!=="desktop"&&(
        <div className="bottom-tabs">
          {bp === "mobile" ? (
            MOBILE_NAV_ITEMS.map(({ id, Icon }) => (
              <button
                key={id}
                className={`tab-btn ${mobileNav === id ? "on" : ""}`}
                onClick={() => handleMobileNav(id)}
              >
                {id === "messages" && unreadCount > 0 && mobileNav !== "messages" && (
                  <span className="tab-badge" style={{ background:C.rose, right:"calc(50% - 16px)" }}>
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
                <Icon/>
              </button>
            ))
          ) : (
            TABS.map(t=>(
              <button key={t.id} className={`tab-btn ${tab===t.id?"on":""}`} onClick={()=>handleTabChange(t.id)}>
                {t.id==="messages" && unreadCount > 0 && tab !== "messages" && (
                  <span className="tab-badge" style={{ background:C.rose }}>{unreadCount > 9 ? "9+" : unreadCount}</span>
                )}
                <span className="tab-icon">{t.icon}</span>{t.label}
              </button>
            ))
          )}
        </div>
      )}

      {showPostForm&&(
        <PostCallForm
          profile={profile}
          onClose={()=>setShowPostForm(false)}
          onSaved={()=>{ refreshCallouts(); }}
          calloutLimitReached={calloutLimitReached}
        />
      )}

      {showUpgradePrompt&&(
        <div style={{ position:"fixed",inset:0,zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(40,30,20,.55)",padding:"24px 16px" }} onClick={()=>setShowUpgradePrompt(false)}>
          <div style={{ background:C.cream,border:`1px solid ${C.clay}`,borderRadius:12,width:"100%",maxWidth:440,padding:"28px 26px",boxShadow:"0 12px 48px rgba(0,0,0,.22)" }} onClick={e=>e.stopPropagation()}>
            <h2 style={{ fontFamily:"'Fraunces',serif",fontSize:"1.5rem",fontWeight:400,color:C.bark,margin:"0 0 12px" }}>Upgrade to post more calls</h2>
            <p style={{ fontFamily:"'DM Sans',sans-serif",fontSize:".88rem",color:C.muted,lineHeight:1.65,margin:"0 0 22px" }}>
              You've used your 3 free listings. Upgrade to Emerging to post unlimited open calls and unlock the full application dashboard.
            </p>
            <div style={{ display:"flex",gap:10 }}>
              <button className="btn-g" style={{ flex:1,fontSize:".7rem",padding:"11px" }} onClick={()=>setShowUpgradePrompt(false)}>Not now</button>
              <button className="btn-p" style={{ flex:2,fontSize:".7rem",padding:"11px" }} onClick={()=>{ setShowUpgradePrompt(false); setTab("plan"); setMobileNav("profile"); setGalProfileSubView("plan"); }}>View plans →</button>
            </div>
          </div>
        </div>
      )}

      {editingCallout&&(
        <PostCallForm
          profile={profile}
          existingOpportunity={editingCallout}
          onClose={()=>setEditingCallout(null)}
          onSaved={()=>refreshCallouts()}
        />
      )}

      {selectedCallout&&(
        <div style={{ position:"fixed",inset:0,zIndex:200,display:"flex",alignItems:"flex-start",justifyContent:"center",background:"rgba(40,30,20,.55)",overflowY:"auto",padding:"24px 16px" }} onClick={()=>setSelectedCallout(null)}>
          <div style={{ background:C.parch,borderRadius:12,width:"100%",maxWidth:620,padding:"28px 24px",boxShadow:"0 8px 40px rgba(0,0,0,.18)",marginBottom:24 }} onClick={e=>e.stopPropagation()}>

            {/* Header */}
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16 }}>
              <div>
                {(()=>{ const ts=TYPE_STYLE[selectedCallout.type]||TYPE_STYLE.exhibition; return (
                  <span style={{ background:ts.bg,color:ts.color,fontFamily:"'DM Mono',monospace",fontSize:".56rem",letterSpacing:"1.5px",textTransform:"uppercase",padding:"2px 8px",borderRadius:"2px",fontWeight:600 }}>{ts.label}</span>
                ); })()}
                <h2 style={{ fontFamily:"'Fraunces',serif",fontSize:"1.5rem",fontWeight:400,color:C.bark,margin:"8px 0 4px" }}>{selectedCallout.title}</h2>
                <div style={{ fontSize:".72rem",color:C.muted,fontFamily:"'DM Mono',monospace" }}>
                  {[selectedCallout.org_name, selectedCallout.location, selectedCallout.state].filter(Boolean).join(" · ")}
                </div>
              </div>
              <button onClick={()=>setSelectedCallout(null)} style={{ background:"none",border:"none",fontSize:"1.2rem",cursor:"pointer",color:C.muted,lineHeight:1,padding:4,marginLeft:12,flexShrink:0 }}>✕</button>
            </div>

            {/* Status + toggle */}
            <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:20 }}>
              <span style={{ background:selectedCallout.status==='active'?C.sageL:C.parchD,color:selectedCallout.status==='active'?C.sageD:C.muted,fontFamily:"'DM Mono',monospace",fontSize:".56rem",letterSpacing:"1.5px",textTransform:"uppercase",padding:"3px 9px",borderRadius:"2px",fontWeight:600 }}>
                {selectedCallout.status}
              </span>
              <button
                onClick={toggleCalloutStatus}
                disabled={togglingStatus}
                style={{ background:"none",border:`1px solid ${C.sand}`,borderRadius:4,padding:"4px 12px",fontSize:".62rem",fontFamily:"'DM Mono',monospace",letterSpacing:"1px",textTransform:"uppercase",color:C.muted,cursor:"pointer" }}
              >
                {togglingStatus ? "…" : selectedCallout.status==='active' ? "Close call" : "Reopen"}
              </button>
            </div>

            {/* Theme */}
            {selectedCallout.theme&&(
              <p style={{ fontSize:".88rem",color:C.bark,lineHeight:1.7,marginBottom:16,paddingBottom:16,borderBottom:`1px solid ${C.sand}` }}>{selectedCallout.theme}</p>
            )}

            {/* Details grid */}
            <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:14 }}>
              {[
                ["Deadline",     selectedCallout.deadline ? `${formatDate(selectedCallout.deadline)}${selectedCallout.deadline_time ? ` · ${selectedCallout.deadline_time}` : ''}` : "—"],
                ["Entry fee",    selectedCallout.entry_fee_type==='free' ? "Free" : `$${selectedCallout.entry_fee_amount} AUD`],
                ["Prize",        fmtPrize(selectedCallout.prize)],
                ["Commission",   fmtCommission(selectedCallout.commission)],
                ["Eligibility",  selectedCallout.eligibility  || "—"],
                ["ABN required", selectedCallout.abn_required ? "Yes" : "No"],
              ].map(([k,v])=>(
                <div key={k} style={{ background:C.cream,border:`1px solid ${C.sand}`,borderRadius:6,padding:"10px 12px" }}>
                  <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".54rem",letterSpacing:"1.5px",textTransform:"uppercase",color:C.muted,marginBottom:3 }}>{k}</div>
                  <div style={{ fontSize:".82rem",color:C.bark }}>{v}</div>
                </div>
              ))}
            </div>

            {/* Mediums */}
            {selectedCallout.mediums?.length > 0&&(
              <div style={{ marginBottom:14 }}>
                <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".58rem",letterSpacing:"1.5px",textTransform:"uppercase",color:C.muted,marginBottom:6 }}>Mediums</div>
                <div style={{ display:"flex",gap:6,flexWrap:"wrap" }}>
                  {selectedCallout.mediums.map(m=>(
                    <span key={m} style={{ background:C.parchD,color:C.mutedD,fontSize:".7rem",fontFamily:"'DM Mono',monospace",padding:"3px 9px",borderRadius:4 }}>{m}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Max dimensions / artworks */}
            {(selectedCallout.max_dimensions||selectedCallout.max_artworks)&&(
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:14 }}>
                {selectedCallout.max_dimensions&&(
                  <div style={{ background:C.cream,border:`1px solid ${C.sand}`,borderRadius:6,padding:"10px 12px" }}>
                    <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".54rem",letterSpacing:"1.5px",textTransform:"uppercase",color:C.muted,marginBottom:3 }}>Max dimensions</div>
                    <div style={{ fontSize:".82rem",color:C.bark }}>{selectedCallout.max_dimensions}</div>
                  </div>
                )}
                {selectedCallout.max_artworks&&(
                  <div style={{ background:C.cream,border:`1px solid ${C.sand}`,borderRadius:6,padding:"10px 12px" }}>
                    <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".54rem",letterSpacing:"1.5px",textTransform:"uppercase",color:C.muted,marginBottom:3 }}>Max artworks</div>
                    <div style={{ fontSize:".82rem",color:C.bark }}>{selectedCallout.max_artworks}</div>
                  </div>
                )}
              </div>
            )}

            {/* Extra info */}
            {selectedCallout.extra_info&&(
              <div style={{ marginBottom:14 }}>
                <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".58rem",letterSpacing:"1.5px",textTransform:"uppercase",color:C.muted,marginBottom:6 }}>Extra info</div>
                <p style={{ fontSize:".84rem",color:C.bark,lineHeight:1.65,margin:0 }}>{selectedCallout.extra_info}</p>
              </div>
            )}

            {/* Consignment */}
            {selectedCallout.consignment_offered&&(
              <div style={{ background:C.sageL,border:`1px solid ${C.sage}`,borderRadius:8,padding:"14px 16px",marginBottom:14 }}>
                <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".58rem",letterSpacing:"1.5px",textTransform:"uppercase",color:C.sageD,marginBottom:10 }}>Consignment offered</div>
                <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8 }}>
                  {[
                    ["Duration",        selectedCallout.consignment_duration ? `${selectedCallout.consignment_duration} weeks` : "—"],
                    ["Damage policy",   fmtResponsibility(selectedCallout.damage_policy)],
                    ["Return shipping", fmtResponsibility(selectedCallout.return_shipping)],
                    ["Payment terms",   selectedCallout.payment_terms ? `${selectedCallout.payment_terms} days` : "—"],
                  ].map(([k,v])=>(
                    <div key={k} style={{ background:"rgba(255,255,255,.5)",borderRadius:5,padding:"8px 10px" }}>
                      <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".54rem",letterSpacing:"1.5px",textTransform:"uppercase",color:C.sageD,marginBottom:2 }}>{k}</div>
                      <div style={{ fontSize:".8rem",color:C.bark,textTransform:"capitalize" }}>{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div style={{ borderTop:`1px solid ${C.sand}`,paddingTop:16,display:"flex",gap:10 }}>
              <button className="btn-p" style={{ flex:1,fontSize:".68rem",padding:"11px" }} onClick={()=>{ setEditingCallout(selectedCallout); setSelectedCallout(null); }}>Edit</button>
              <button className="btn-g" style={{ flex:1,fontSize:".68rem",padding:"11px" }} onClick={()=>setSelectedCallout(null)}>Close</button>
            </div>

          </div>
        </div>
      )}

      {/* Hamburger drawer — mobile profile only */}
      {hamburgerOpen&&(
        <>
          <div onClick={()=>setHamburgerOpen(false)} style={{position:"fixed",inset:0,background:"rgba(20,13,10,.55)",zIndex:400}}/>
          <div style={{position:"fixed",top:0,right:0,bottom:0,width:"72vw",maxWidth:280,background:C.bark,zIndex:401,display:"flex",flexDirection:"column",boxShadow:"-8px 0 32px rgba(0,0,0,.3)"}}>
            <div style={{display:"flex",justifyContent:"flex-end",padding:"18px 20px",borderBottom:"1px solid rgba(255,255,255,.08)"}}>
              <button onClick={()=>setHamburgerOpen(false)} style={{background:"none",border:"none",color:"rgba(196,168,130,.6)",fontSize:"1.3rem",lineHeight:1,cursor:"pointer",padding:4}}>✕</button>
            </div>
            <div style={{flex:1}}>
              <button onClick={()=>{setHamburgerOpen(false);setShowEditProfile(true);}} style={{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",padding:"17px 24px",background:"none",border:"none",borderBottom:"1px solid rgba(255,255,255,.06)",color:"#e8d5b7",fontSize:".88rem",textAlign:"left",cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
                <span>Edit Profile</span>
                <span style={{color:"rgba(196,168,130,.4)",fontSize:"1.1rem"}}>›</span>
              </button>
              <button onClick={()=>{setHamburgerOpen(false);setGalProfileSubView("plan");}} style={{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",padding:"17px 24px",background:"none",border:"none",borderBottom:"1px solid rgba(255,255,255,.06)",color:"#e8d5b7",fontSize:".88rem",textAlign:"left",cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
                <span>Plan & Billing</span>
                <span style={{color:"rgba(196,168,130,.4)",fontSize:"1.1rem"}}>›</span>
              </button>
              <div style={{borderTop:"1px solid rgba(255,255,255,.06)",margin:"8px 0"}}/>
              <button onClick={onLogout} style={{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",padding:"17px 24px",background:"none",border:"none",color:C.rose,fontSize:".88rem",textAlign:"left",cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
                <span>Log Out</span>
                <span style={{color:"rgba(196,168,130,.4)",fontSize:"1.1rem"}}>›</span>
              </button>
            </div>
          </div>
        </>
      )}

      {showEditProfile&&(
        <EditProfileModal
          profile={profile}
          onClose={()=>setShowEditProfile(false)}
          onSaved={(updated)=>setCurrentProfile(updated)}
        />
      )}

      {pendingAcceptApp&&(
        <ConsignmentConfirmModal
          app={pendingAcceptApp}
          galleryId={profile.id}
          onClose={()=>setPendingAcceptApp(null)}
          onDone={()=>{ setPendingAcceptApp(null); setSelectedApp(null); refreshApplications(); }}
        />
      )}

      {appLoading&&(
        <div style={{ position:"fixed",inset:0,zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(40,30,20,.45)" }}>
          <span style={{ fontFamily:"'DM Mono',monospace",fontSize:".7rem",color:"#f0e2cc",letterSpacing:"2px",textTransform:"uppercase" }}>Loading…</span>
        </div>
      )}

      {selectedApp&&(
        <div style={{ position:"fixed",inset:0,zIndex:200,display:"flex",alignItems:"flex-start",justifyContent:"center",background:"rgba(40,30,20,.55)",overflowY:"auto",padding:"24px 16px" }} onClick={()=>setSelectedApp(null)}>
          <div style={{ background:C.parch,borderRadius:12,width:"100%",maxWidth:600,padding:"28px 24px",boxShadow:"0 8px 40px rgba(0,0,0,.18)" }} onClick={e=>e.stopPropagation()}>

            {/* Header */}
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20 }}>
              <h2 style={{ fontFamily:"'Fraunces',serif",fontSize:"1.4rem",fontWeight:400,color:C.bark,margin:0 }}>Application</h2>
              <button onClick={()=>setSelectedApp(null)} style={{ background:"none",border:"none",fontSize:"1.2rem",cursor:"pointer",color:C.muted,lineHeight:1,padding:4 }}>✕</button>
            </div>

            {/* Status badge */}
            {(()=>{ const sc=STATUS_COLORS[selectedApp.status]||STATUS_COLORS.pending; return (
              <div style={{ marginBottom:20 }}>
                <span style={{ background:sc.bg,color:sc.c,fontFamily:"'DM Mono',monospace",fontSize:".56rem",letterSpacing:"1.5px",textTransform:"uppercase",padding:"3px 9px",borderRadius:"2px",fontWeight:600 }}>{selectedApp.status}</span>
              </div>
            ); })()}

            {/* Artist details */}
            <div style={{ marginBottom:20 }}>
              <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".62rem",letterSpacing:"1px",textTransform:"uppercase",color:C.muted,marginBottom:10 }}>Artist</div>
              <div style={{ fontFamily:"'Fraunces',serif",fontSize:"1.15rem",color:C.bark,marginBottom:4 }}>
                {selectedApp.artist?.display_name||selectedApp.artist?.full_name||'Artist'}
              </div>
              {selectedApp.artist?.location&&(
                <div style={{ fontSize:".78rem",color:C.muted,marginBottom:6 }}>{selectedApp.artist.location}</div>
              )}
              {selectedApp.artist?.bio&&(
                <p style={{ fontSize:".82rem",color:C.bark,lineHeight:1.65,margin:"0 0 10px" }}>{selectedApp.artist.bio}</p>
              )}
              <div style={{ display:"flex",gap:14,flexWrap:"wrap" }}>
                {selectedApp.artist?.instagram&&(
                  <a href={`https://instagram.com/${selectedApp.artist.instagram}`} target="_blank" rel="noreferrer" style={{ fontSize:".72rem",color:C.clay,fontFamily:"'DM Mono',monospace",textDecoration:"none" }}>📷 @{selectedApp.artist.instagram}</a>
                )}
                {selectedApp.artist?.website&&(
                  <a href={selectedApp.artist.website} target="_blank" rel="noreferrer" style={{ fontSize:".72rem",color:C.clay,fontFamily:"'DM Mono',monospace",textDecoration:"none" }}>🌐 {selectedApp.artist.website.replace(/^https?:\/\//,'')}</a>
                )}
              </div>
            </div>

            <div style={{ borderTop:`1px solid ${C.sand}`,marginBottom:20 }}/>

            {/* Application details */}
            <div style={{ marginBottom:20 }}>
              <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".62rem",letterSpacing:"1px",textTransform:"uppercase",color:C.muted,marginBottom:10 }}>Application</div>
              <div style={{ fontSize:".78rem",color:C.muted,fontFamily:"'DM Mono',monospace",marginBottom:14 }}>
                {selectedApp.opportunities?.title} · {new Date(selectedApp.submitted_at).toLocaleDateString('en-AU')}
              </div>
              {selectedApp.artist_statement&&(
                <div style={{ marginBottom:14 }}>
                  <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".58rem",letterSpacing:"1px",textTransform:"uppercase",color:C.muted,marginBottom:5 }}>Artist Statement</div>
                  <p style={{ fontSize:".84rem",color:C.bark,lineHeight:1.7,margin:0 }}>{selectedApp.artist_statement}</p>
                </div>
              )}
              {selectedApp.theme_response&&(
                <div style={{ marginBottom:14 }}>
                  <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".58rem",letterSpacing:"1px",textTransform:"uppercase",color:C.muted,marginBottom:5 }}>Theme Response</div>
                  <p style={{ fontSize:".84rem",color:C.bark,lineHeight:1.7,margin:0 }}>{selectedApp.theme_response}</p>
                </div>
              )}
              {selectedApp.selected_mediums?.length>0&&(
                <div>
                  <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".58rem",letterSpacing:"1px",textTransform:"uppercase",color:C.muted,marginBottom:6 }}>Mediums</div>
                  <div style={{ display:"flex",gap:6,flexWrap:"wrap" }}>
                    {selectedApp.selected_mediums.map(m=>(
                      <span key={m} style={{ background:C.parchD,color:C.bark,fontSize:".7rem",fontFamily:"'DM Mono',monospace",padding:"3px 9px",borderRadius:4 }}>{m}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div style={{ borderTop:`1px solid ${C.sand}`,marginBottom:20 }}/>

            {/* Status controls */}
            <div style={{ display:"flex",gap:8,flexWrap:"wrap" }}>
              <button className="btn-dk" style={{ flex:1,fontSize:".68rem",padding:"10px 8px" }} onClick={()=>setSelectedApp(null)} disabled={updatingStatus}>← Back</button>
              <button onClick={()=>updateStatus('shortlisted')} disabled={updatingStatus} style={{ flex:1,fontSize:".68rem",padding:"10px 8px",borderRadius:6,border:"none",background:"#e8d5f5",color:"#5a2d82",cursor:"pointer",fontFamily:"'DM Mono',monospace",letterSpacing:"1px",textTransform:"uppercase",fontWeight:600 }}>Shortlist</button>
              <button onClick={()=>setPendingAcceptApp(selectedApp)} disabled={updatingStatus} style={{ flex:1,fontSize:".68rem",padding:"10px 8px",borderRadius:6,border:"none",background:C.sageL,color:C.sageD,cursor:"pointer",fontFamily:"'DM Mono',monospace",letterSpacing:"1px",textTransform:"uppercase",fontWeight:600 }}>Accept</button>
              <button onClick={()=>updateStatus('declined')} disabled={updatingStatus} style={{ flex:1,fontSize:".68rem",padding:"10px 8px",borderRadius:6,border:"none",background:C.roseL,color:C.rose,cursor:"pointer",fontFamily:"'DM Mono',monospace",letterSpacing:"1px",textTransform:"uppercase",fontWeight:600 }}>Decline</button>
            </div>

          </div>
        </div>
      )}
    </>
  );
}
